import { patientDisplayName } from 'utils/orders';
import React, { FC } from 'react';
import { Box, Grid, Paper, Typography, Divider, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { restorationActionRows } from 'utils/restoration-price-rows';

type OrderInfoBoxProps = {
    order: Order;
};

const OrderInfoBox: FC<OrderInfoBoxProps> = ({ order }) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    const patient = order.patient;
    const patientName = patientDisplayName(patient) || '-';
    const patientId = patient?.personalIdentityNumber || '-';
    // Show the clinic the order is delivered to, not the main clinic.
    const clinic = order.deliveryDetails?.destination || order.customer;
    const clinicName = clinic?.name || '-';
    const clinicPhone = clinic?.contact?.communication?.phone;
    const dentistName = order.dentist?.fullName || '-';
    const dentistPhone = order.dentist?.phone;
    const sampleType = order.deliveryDetails?.sample?.type || order.deliveryDetails?.sampleType;
    const sampleContent = order.deliveryDetails?.sample?.content?.filter((c) => c.value) || [];
    const actions = restorationActionRows(order.restorations);

    return (
        <Paper variant="outlined" square sx={{ mb: 2 }}>
            <Box p={2}>
                <Typography variant="h3" mb={1}>
                    {translate('orders:ORDER')} {order.orderId}
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">{translate('orders:PATIENT')}</Typography>
                        <Typography variant="body2">{patientName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {patientId}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">{translate('orders:CLINIC_AND_DENTIST')}</Typography>
                        <Typography variant="body2">{clinicName}</Typography>
                        {clinicPhone && (
                            <Typography variant="body2">
                                {translate('common:PHONE')}: {clinicPhone}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                            {dentistName}
                        </Typography>
                        {dentistPhone && (
                            <Typography variant="body2" color="text.secondary">
                                {translate('common:PHONE')}: {dentistPhone}
                            </Typography>
                        )}
                    </Grid>
                    {sampleType && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">{translate('orders:SAMPLE_TAKEN')}</Typography>
                            <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                <Chip label={translate(`orders:${sampleType}`)} size="small" />
                                {sampleContent.map((c) => (
                                    <Chip
                                        key={c.name}
                                        label={translate(`orders:${c.name}`)}
                                        size="small"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Grid>
                    )}
                    {actions.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">{translate('orders:ACTIONS')}</Typography>
                            {actions.map((item) => (
                                <Typography
                                    key={item._id}
                                    variant="body2"
                                    noWrap
                                    title={`${item.code || ''} - ${item.name}`}
                                >
                                    {item.code} - {item.name}
                                </Typography>
                            ))}
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Paper>
    );
};

export default OrderInfoBox;
